// Created: 2026-04-22 by Douglas
// Live comms feed panel — inbound SMS, calls, emails surfaced in real-time.
// Supabase Realtime on comms_feed table. Claim = sets handled_by + status active.
// Renders into document.body via createPortal (same pattern as ActivityFeed).

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  IconPhone,
  IconMessage,
  IconMail,
  IconX,
  IconCheck,
  IconAlertCircle,
  IconRadio,
} from '@tabler/icons-react';

// ─── Chime ───────────────────────────────────────────────────────────────────
function playChime(type) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    if (type === 'call') {
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(660, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } else {
      osc.frequency.setValueAtTime(1047, ctx.currentTime);
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.35);
    }
  } catch {
    // Blocked or not supported
  }
}

// ─── Channel config ───────────────────────────────────────────────────────────
const CHANNEL_CONFIG = {
  sms:            { Icon: IconMessage, color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/25' },
  call:           { Icon: IconPhone,   color: 'text-red-400',    bg: 'bg-red-500/10',    border: 'border-red-500/25' },
  email:          { Icon: IconMail,    color: 'text-gray-400',   bg: 'bg-gray-500/10',   border: 'border-gray-500/25' },
  chat:           { Icon: IconMessage, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/25' },
  ticket_message: { Icon: IconMessage, color: 'text-amber-400',  bg: 'bg-amber-500/10',  border: 'border-amber-500/25' },
};

const STATUS_LABEL = {
  ringing: 'Ringing', active: 'Active', unhandled: 'Unhandled',
  handled: 'Handled', auto_handled: 'Auto', missed: 'Missed', completed: 'Done',
};

const FILTERS       = ['all', 'call', 'sms', 'email'];
const FILTER_LABELS = { all: 'All', call: 'Calls', sms: 'SMS', email: 'Email' };

// ─── CommItem ─────────────────────────────────────────────────────────────────
function CommItem({ item, onClaim, currentUserId }) {
  const cfg = CHANNEL_CONFIG[item.channel] || CHANNEL_CONFIG.sms;
  const { Icon } = cfg;

  const isRinging     = item.status === 'ringing';
  const isActionable  = item.status === 'unhandled' || item.status === 'ringing';
  const isClaimedByMe =
    item.handled_by === currentUserId &&
    (item.status === 'active' || item.status === 'handled');

  const fromDisplay = item.from_name || item.from_identifier || 'Unknown';

  let timeAgo = '';
  if (item.created_at) {
    try { timeAgo = formatDistanceToNow(new Date(item.created_at), { addSuffix: true }); } catch {}
  }

  return (
    <div
      className={`
        relative rounded-lg border p-3 mb-2 transition-all duration-200
        ${cfg.bg} ${cfg.border}
        ${isRinging ? 'shadow-[0_0_14px_rgba(239,68,68,0.25)]' : ''}
      `}
    >
      {isRinging && (
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
        </span>
      )}

      <div className="flex items-start gap-2.5">
        <div className={`mt-0.5 flex-shrink-0 ${cfg.color} ${isRinging ? 'animate-pulse' : ''}`}>
          <Icon className="h-4 w-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1 mb-0.5">
            <span className="text-[12px] font-semibold text-white truncate">{fromDisplay}</span>
            <span className="text-[10px] text-gray-500 flex-shrink-0">{timeAgo}</span>
          </div>

          {item.location_name && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-gray-800 text-gray-400 border border-gray-700 mb-1">
              {item.location_name}
            </span>
          )}

          {(item.subject || item.preview) && (
            <p className="text-[11px] text-gray-400 line-clamp-2 mt-0.5 leading-relaxed">
              {item.subject || item.preview}
            </p>
          )}

          <div className="flex items-center justify-between mt-2">
            <span className={`text-[10px] font-medium uppercase tracking-wide ${cfg.color}`}>
              {STATUS_LABEL[item.status] || item.status}
            </span>

            {isActionable && (
              <button
                onClick={() => onClaim(item.id)}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-[#AEA36D]/20 hover:bg-[#AEA36D]/35 text-[#AEA36D] border border-[#AEA36D]/30 transition-all duration-150 active:scale-95"
              >
                <IconCheck className="h-3 w-3" />
                Claim
              </button>
            )}

            {isClaimedByMe && (
              <span className="text-[10px] text-emerald-400 font-medium">You claimed</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── useCommsUnhandledCount ───────────────────────────────────────────────────
export function useCommsUnhandledCount() {
  const [count, setCount]     = useState(0);
  const [hasCall, setHasCall] = useState(false);

  useEffect(() => {
    let channel;

    // Exact head counts. The old query selected every ringing/unhandled inbound ROW (PostgREST
    // returns at most 1,000) on every insert/update from every open Truest tab, while 11,353 old
    // rows sit "unhandled" with nobody to claim them. Count-only, coalesced.
    let timer = null;
    const refreshNow = async () => {
      const base = () => supabase
        .from('comms_feed')
        .select('id', { count: 'exact', head: true })
        .in('status', ['ringing', 'unhandled'])
        .eq('direction', 'inbound');
      const [all, calls] = await Promise.all([base(), base().eq('channel', 'call')]);
      if (all.error) return;
      setCount(all.count || 0);
      setHasCall((calls.count || 0) > 0);
    };
    const refresh = () => { clearTimeout(timer); timer = setTimeout(refreshNow, 800); };

    refreshNow();

    channel = supabase
      .channel('comms-badge-count')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'comms_feed' }, refresh)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'comms_feed' }, refresh)
      .subscribe();

    return () => { clearTimeout(timer); supabase.removeChannel(channel); };
  }, []);

  return { count, hasCall };
}

// ─── CommsLivePanel ───────────────────────────────────────────────────────────
export default function CommsLivePanel({ open, onClose }) {
  const { user, valetProfile } = useAuth();
  const [items, setItems]      = useState([]);
  const [loading, setLoading]  = useState(true);
  const [filter, setFilter]    = useState('all');

  const currentUserId = valetProfile?.id || user?.id;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('comms_feed')
      .select('*')
      .in('status', ['ringing', 'unhandled', 'active'])
      .eq('direction', 'inbound')
      .order('created_at', { ascending: false })
      .limit(50);

    if (!error && data) setItems(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchItems();
  }, [open, fetchItems]);

  useEffect(() => {
    const channel = supabase
      .channel('comms-live-panel')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'comms_feed', filter: 'direction=eq.inbound' },
        (payload) => {
          const row = payload.new;
          setItems(prev => {
            if (prev.some(r => r.id === row.id)) return prev;
            return [row, ...prev].slice(0, 50);
          });
          if (row.status === 'ringing' || row.status === 'unhandled') {
            playChime(row.channel === 'call' ? 'call' : 'sms');
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'comms_feed' },
        (payload) => {
          const row = payload.new;
          setItems(prev => {
            if (row.status === 'completed' || row.status === 'auto_handled') {
              return prev.filter(r => r.id !== row.id);
            }
            return prev.map(r => r.id === row.id ? row : r);
          });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const handleClaim = useCallback(async (commId) => {
    if (!currentUserId) return;

    await supabase
      .from('comms_feed')
      .update({
        handled_by: currentUserId,
        status:     'active',
        handled_at: new Date().toISOString(),
      })
      .eq('id', commId);

    setItems(prev =>
      prev.map(r => r.id === commId ? { ...r, status: 'active', handled_by: currentUserId } : r)
    );
  }, [currentUserId]);

  const filteredItems   = filter === 'all' ? items : items.filter(r => r.channel === filter);
  const unhandledCount  = items.filter(r => r.status === 'ringing' || r.status === 'unhandled').length;

  if (!open) return null;

  const panel = (
    <div
      className="fixed right-0 z-[9990] flex flex-col w-80 bg-gray-950 border-l border-gray-800 shadow-2xl"
      style={{ top: '44px', bottom: 0 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 flex-shrink-0 bg-black/40">
        <div className="flex items-center gap-2">
          <IconRadio className="h-4 w-4 text-[#AEA36D]" />
          <span className="text-sm font-semibold text-white">Live Comms</span>
          {unhandledCount > 0 && (
            <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full text-[10px] font-bold bg-red-500 text-white">
              {unhandledCount > 99 ? '99+' : unhandledCount}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-500 hover:text-white hover:bg-gray-800 rounded transition-colors"
        >
          <IconX className="h-4 w-4" />
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 px-3 py-2 border-b border-gray-800 flex-shrink-0">
        {FILTERS.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`
              flex-1 py-1 rounded text-[11px] font-medium transition-colors
              ${filter === f
                ? 'bg-[#AEA36D]/20 text-[#AEA36D] border border-[#AEA36D]/30'
                : 'text-gray-500 hover:text-gray-300 border border-transparent'
              }
            `}
          >
            {FILTER_LABELS[f]}
          </button>
        ))}
      </div>

      {/* Feed */}
      <ScrollArea className="flex-1 px-3 py-2">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#AEA36D]" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <IconAlertCircle className="h-8 w-8 text-gray-700 mb-2" />
            <p className="text-sm text-gray-600">No active comms</p>
            <p className="text-[11px] text-gray-700 mt-0.5">
              {filter !== 'all' ? `No ${FILTER_LABELS[filter].toLowerCase()} right now` : 'Quiet out there'}
            </p>
          </div>
        ) : (
          filteredItems.map(item => (
            <CommItem key={item.id} item={item} onClaim={handleClaim} currentUserId={currentUserId} />
          ))
        )}
      </ScrollArea>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-gray-800 flex-shrink-0">
        <p className="text-[10px] text-gray-600 text-center">Live — Supabase Realtime</p>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}